import type React from 'react';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import isEmpty from 'utils/isEmpty';
import countryList from 'utils/countryList';
import styles from './InvoicingForm.module.css';
import InputText from 'components/library/Input/InputText/InputText';
import LocationInput from 'components/library/Input/LocationInput/LocationInput';
import Switch from 'components/library/Input/Switch/Switch';
import type { UserState } from 'services/store/features/User/types';

type Error = {
  firstName?: string;
  lastName?: string;
  address?: string;
};

interface Props {
  user: UserState;
  setIsComplete: (value: boolean) => void;
};

export default function UserForm({
  user, setIsComplete
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [errors, setErrors] = useState<Error>({});

  const validate = () => {
    const newErrors: Error = {};
    if (user.firstName && (user.firstName.trim() === '' || user.firstName.length > 64)) {
      newErrors.firstName = t('invoicingForm.firstNameInvalid');
    } if (user.lastName && (user.lastName.trim() === '' || user.lastName.length > 64)) {
      newErrors.lastName = t('invoicingForm.lastNameInvalid');
    } if (user.address.address && (user.address.address.trim() === '' || user.address.address.length > 256)) {
      newErrors.address = t('invoicingForm.addressInvalid');
    } if (user.address.city && (user.address.city.trim() === '' || user.address.city.length > 64)) {
      newErrors.address = t('invoicingForm.cityInvalid');
    } if (user.address.zip_code && (user.address.zip_code.trim() === '' || user.address.zip_code.length > 64)) {
      newErrors.address = t('invoicingForm.zipCodeInvalid');
    } if (user.address.country && (user.address.country.trim() === '' || user.address.country.length !== 2 || !countryList.includes(user.address.country))) {
      newErrors.address = t('invoicingForm.countryInvalid');
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  useEffect(() => {
    const toCheck = [
      user.firstName, user.lastName, user.address.address,
      user.address.city, user.address.zip_code, user.address.country,
    ];
    const completed = toCheck.map((i) => !isEmpty(i)).reduce((c, a) => c && a, true);
    if (user.fromCompany || (completed && validate())) {
      setIsComplete(true);
    } else {
      setIsComplete(false);
    }
  }, [
    dispatch, user.fromCompany,
    user.firstName, user.lastName, user.companyLegalName, user.siren,
    user.address.address, user.address.city, user.address.zip_code, user.address.country,
  ]);

  const switchCompanyChange = (value: boolean) => {
    setErrors({});
    dispatch({ type: 'UPDATE_USER_FROM_COMPANY', payload: value });
  };

  const handleInputForm = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: e.target.name, payload: e.target.value });
  };

  return (
    <>
      <Switch
        className={styles.switchCompany}
        name="from_company"
        label={t('invoicingForm.switchCompany')}
        checked={user.fromCompany}
        onChange={switchCompanyChange}
      />
      {
        user.fromCompany ? (<></>) : (
          <>
            <div className={styles.row}>
              <InputText
                className={`${styles.bold} ${styles.inputContainer}`}
                name="UPDATE_USER_FIRST_NAME"
                label={t('invoicingForm.firstName')}
                valueInput={user.firstName}
                onTextChange={handleInputForm}
                state={errors?.firstName ? 'ERROR' : 'DEFAULT'}
                hint={errors?.firstName}
                required
              />
              <InputText
                className={`${styles.bold} ${styles.inputContainer}`}
                name="UPDATE_USER_LAST_NAME"
                label={t('invoicingForm.lastName')}
                valueInput={user.lastName}
                onTextChange={handleInputForm}
                state={errors?.lastName ? 'ERROR' : 'DEFAULT'}
                hint={errors?.lastName}
                required
              />
            </div>
            <LocationInput
              className={styles.bold}
              values={user.address}
              handleChange={(e) => dispatch({
                type: 'UPDATE_USER_ADDRESS',
                payload: { name: e.target.name, value: e.target.value },
              })}
              setNewValues={(name, value) => dispatch({
                type: 'UPDATE_USER_ADDRESS',
                payload: { name, value },
              })}
              state={(errors?.address) ? 'ERROR' : 'DEFAULT'}
              hint={errors?.address}
            />
          </>
        )
      }
    </>
  );
}
